import React from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { logger } from '../services/loggingService';

interface AuthErrorBoundaryProps {
  children: React.ReactNode;
}

const AuthErrorFallback = () => (
  <Box sx={{ p: 3 }}>
    <Alert severity="error">
      <AlertTitle>Authentication Error</AlertTitle>
      <Box>
        <p>We're having trouble connecting to our authentication service.</p>
        <p>This could be due to:</p>
        <ul style={{ marginLeft: 20 }}>
          <li>Network connectivity issues</li>
          <li>Firebase service temporary unavailability</li>
          <li>Browser security settings blocking the connection</li>
        </ul>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Box>
      </Box>
    </Alert>
  </Box>
);

// Custom error boundary that only catches critical Firebase initialization errors
class SelectiveAuthErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ComponentType },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ComponentType }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Only catch critical Firebase/network errors that prevent app initialization
    const isCriticalError = 
      error.message?.includes('Firebase: Error (auth/network-request-failed)') ||
      error.message?.includes('Firebase app named') ||
      error.message?.includes('No Firebase App') ||
      error.message?.includes('firestore/unavailable') ||
      error.message?.includes('Failed to get document because the client is offline') ||
      (error.name === 'FirebaseError' && error.message?.includes('network'));

    if (isCriticalError) {
      console.error('Critical Firebase error caught by AuthErrorBoundary:', error);
      return { hasError: true, error };
    }
    
    // Let all other errors bubble up (including normal auth flow errors)
    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Two sinks: console for local DevTools, loggingService for Cloud
    // Logging visibility. Auth failures in prod were invisible
    // server-side before — users saw the fallback Alert but we saw
    // nothing in Logs Explorer.
    console.error('AuthErrorBoundary caught critical error:', error, errorInfo);
    logger.error('AuthErrorBoundary caught Firebase/auth error', {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack?.split('\n').slice(0, 10).join('\n'),
      componentStack: errorInfo.componentStack?.split('\n').slice(0, 15).join('\n'),
      url: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  }

  render() {
    if (this.state.hasError) {
      return <this.props.fallback />;
    }

    return this.props.children;
  }
}

const AuthErrorBoundary: React.FC<AuthErrorBoundaryProps> = ({ children }) => {
  return (
    <SelectiveAuthErrorBoundary fallback={AuthErrorFallback}>
      {children}
    </SelectiveAuthErrorBoundary>
  );
};

export default AuthErrorBoundary;